/**
 * use-users.ts — React Query hooks para Gestão de Usuários (Brief 5 A1, W3.1).
 *
 * Patterns:
 *   - react-query com queryKey tipado.
 *   - Capability gate defensivo: se users-read ausente, data=undefined silenciosamente.
 *   - If-Match ADR-071 passado via header em PATCH (updateUser).
 *   - staleTime 60s (padrão dos hooks de atendimento pós-Fatia G).
 *   - fetch nativo (sem axios) alinhado com api-client.ts existente.
 *
 * Decisão executiva W3.1-D2: hooks locais em src/hooks/ (fora de features/)
 * para reutilização cross-feature (pattern useInboxFilters + useSupervisorDashboard).
 *
 * Decisão executiva W3.1-D3: métodos fetch locais (em vez de estender api object)
 * porque api-client.ts não expõe callJson internamente — reutiliza o mesmo padrão
 * de headers/URL estabelecido no module (ENV + tenant-slug + bearer).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { engineUrl } from '../lib/runtime-config';
import { useAuthStore } from '../stores/auth-store';

import { useCapability } from './use-capability';

import type {
  CreateUserBody,
  DeactivateUserBody,
  HierarchyNodeWire,
  InviteCreatedWire,
  InviteTokenWireItem,
  InviteUserBody,
  ListUsersParams,
  PasswordResetLinkWire,
  ListUsersResponse,
  UpdateUserBody,
  UserWireItem,
} from '../features/admin/users/types';

// ─── Helpers internos ────────────────────────────────────────────────────────

function getEngineUrl(): string {
  return engineUrl();
}

function resolveTenantSlug(): string {
  if (typeof window === 'undefined') return 'netcomet';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
    return 'netcomet';
  }
  return host.split('.')[0] ?? 'netcomet';
}

function buildHeaders(ifMatchVersion?: number): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': resolveTenantSlug(),
  };
  if (token !== null && token.length > 0) headers.Authorization = `Bearer ${token}`;
  if (ifMatchVersion !== undefined) headers['If-Match'] = ifMatchVersion.toString();
  return headers;
}

async function apiFetch<T>(
  path: string,
  init: RequestInit & { ifMatchVersion?: number } = {},
): Promise<T> {
  const { ifMatchVersion, ...rest } = init;
  const headers = buildHeaders(ifMatchVersion);
  const res = await fetch(`${getEngineUrl()}${path}`, { ...rest, headers });
  if (!res.ok) {
    let errBody: unknown = {};
    try {
      errBody = await res.json();
    } catch {
      /* ignore */
    }
    throw Object.assign(new Error(`HTTP ${res.status.toString()}`), {
      status: res.status,
      body: errBody,
    });
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── API functions ───────────────────────────────────────────────────────────

function listUsers(params: ListUsersParams): Promise<ListUsersResponse> {
  const qs = new URLSearchParams();
  if (params.cursor !== undefined) qs.set('cursor', params.cursor);
  if (params.limit !== undefined) qs.set('limit', params.limit.toString());
  if (params.status !== undefined) qs.set('status', params.status);
  if (params.role !== undefined) qs.set('role', params.role);
  if (params.search !== undefined) qs.set('search', params.search);
  const query = qs.toString();
  return apiFetch<ListUsersResponse>(`/v1/users${query.length > 0 ? `?${query}` : ''}`, {
    method: 'GET',
  });
}

const getUserById = (id: string): Promise<UserWireItem> =>
  apiFetch<UserWireItem>(`/v1/users/${id}`, { method: 'GET' });

const getUserHierarchy = (id: string): Promise<HierarchyNodeWire> =>
  apiFetch<HierarchyNodeWire>(`/v1/users/${id}/hierarchy`, { method: 'GET' });

const listInvites = (): Promise<{ data: InviteTokenWireItem[] }> =>
  apiFetch<{ data: InviteTokenWireItem[] }>('/v1/users/invites', { method: 'GET' });

const createUser = (body: CreateUserBody): Promise<UserWireItem> =>
  apiFetch<UserWireItem>('/v1/users', { method: 'POST', body: JSON.stringify(body) });

const updateUser = (id: string, body: UpdateUserBody, version: number): Promise<UserWireItem> =>
  apiFetch<UserWireItem>(`/v1/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    ifMatchVersion: version,
  });

const deactivateUser = (id: string, body: DeactivateUserBody): Promise<UserWireItem> =>
  apiFetch<UserWireItem>(`/v1/users/${id}/deactivate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

const generatePasswordResetLink = (userId: string): Promise<PasswordResetLinkWire> =>
  apiFetch<PasswordResetLinkWire>(`/v1/users/${encodeURIComponent(userId)}/password-reset-link`, {
    method: 'POST',
  });

const inviteUser = (body: InviteUserBody): Promise<InviteCreatedWire> =>
  apiFetch<InviteCreatedWire>('/v1/users/invite', { method: 'POST', body: JSON.stringify(body) });

const revokeInvite = (inviteId: string, reason?: string): Promise<InviteTokenWireItem> =>
  apiFetch<InviteTokenWireItem>(`/v1/users/invites/${encodeURIComponent(inviteId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify(reason !== undefined ? { reason } : {}),
  });

const resendInvite = (inviteId: string): Promise<InviteCreatedWire> =>
  apiFetch<InviteCreatedWire>(`/v1/users/invites/${encodeURIComponent(inviteId)}/resend`, {
    method: 'POST',
  });

// ─── Query keys ─────────────────────────────────────────────────────────────

export const usersKeys = {
  all: ['users'] as const,
  list: (params: ListUsersParams) => ['users', 'list', params] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
  hierarchy: (id: string) => ['users', 'hierarchy', id] as const,
  invites: () => ['users', 'invites'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * useUsers — lista paginada de usuários.
 * Capability gate: users-read. Se ausente, retorna data=undefined silenciosamente.
 * `enabled` (S4 PR-b — Corrente de Compromissos): opt-out adicional do CALLER
 * (ex.: `PortfolioSection` só busca operadores quando o operador tem
 * `customer-portfolio-manage` — gatear o fetch, não só o render). Default
 * `true` preserva o comportamento pré-existente (só o gate de `users-read`).
 */
export function useUsers(params: ListUsersParams = {}, enabled = true) {
  const canRead = useCapability('users-read');

  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => listUsers(params),
    enabled: enabled && canRead,
    staleTime: 60 * 1000,
  });
}

/**
 * useUser — detalhe de um usuário por ID.
 * Capability gate: users-read.
 */
export function useUser(id: string | null) {
  const canRead = useCapability('users-read');

  return useQuery({
    queryKey: usersKeys.detail(id ?? ''),
    queryFn: () => getUserById(id!),
    enabled: canRead && id !== null,
    staleTime: 60 * 1000,
  });
}

/**
 * useUserHierarchy — árvore hierárquica (managers + directs) de um usuário.
 * Capability gate: users-read.
 */
export function useUserHierarchy(userId: string | null) {
  const canRead = useCapability('users-read');

  return useQuery({
    queryKey: usersKeys.hierarchy(userId ?? ''),
    queryFn: () => getUserHierarchy(userId!),
    enabled: canRead && userId !== null,
    staleTime: 60 * 1000,
  });
}

/**
 * useInvites — lista de InviteTokens do tenant.
 * Capability gate: users-invite (só quem pode convidar vê).
 */
export function useInvites() {
  const canInvite = useCapability('users-invite');

  return useQuery({
    queryKey: usersKeys.invites(),
    queryFn: () => listInvites(),
    enabled: canInvite,
    staleTime: 60 * 1000,
  });
}

/**
 * useCreateUser — mutation para criar usuário.
 * Capability gate: users-create.
 * Invalida lista após sucesso.
 */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => createUser(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/**
 * useUpdateUser — mutation para atualizar usuário (If-Match ADR-071).
 * Invalida detalhe + lista após sucesso.
 */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, version }: { id: string; body: UpdateUserBody; version: number }) =>
      updateUser(id, body, version),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: usersKeys.detail(updated.id) });
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/**
 * useDeactivateUser — mutation para desativar usuário (soft-delete).
 * Invalida detalhe + lista após sucesso.
 */
export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DeactivateUserBody }) =>
      deactivateUser(id, body),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: usersKeys.detail(updated.id) });
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/**
 * useInviteUser — mutation para enviar convite.
 * Invalida lista de convites após sucesso.
 */
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserBody) => inviteUser(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.invites() });
    },
  });
}

/** Cancela um convite pendente (04/Set/2026). Invalida a lista. */
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { inviteId: string; reason?: string }) =>
      revokeInvite(params.inviteId, params.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.invites() });
    },
  });
}

/** Reenvia um convite (segredo e prazo novos). O segredo novo volta na resposta. */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => resendInvite(inviteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.invites() });
    },
  });
}

/** Item 477 — o gestor gera um link de redefinição de senha para o usuário (segredo volta uma vez). */
export function useGeneratePasswordResetLink() {
  return useMutation({ mutationFn: (userId: string) => generatePasswordResetLink(userId) });
}
